import { jsx } from '@amazon/vinyl-tsx'
import { createDisposer } from '@amazon/vinyl-util'
import { data, type MutableValue } from '@amazon/vinyl-observable'
import { authSettled, signedIn } from '@/auth/authRef'
import { isTv } from '@/device/deviceConfig'
import { getUserInfo, pickProfileImageUrl } from '@/customer/service/userInfoService'
import { getString } from '@/header/i18n/bundle.generated'
import { reportShellClick } from '@/metrics/shellUiMetrics'
import { Icon } from '@/components/Icon'
import { watchLoadableUrl } from '@/components/artImage'

export function ProfileAvatarButton(props: {
    headerMenuVisible: MutableValue<boolean>
    // Extra class tokens for callers that need to size/position the shared
    // avatar (e.g. the mobile top bar) without forking the component.
    className?: string
}) {
    const imageUrl$: MutableValue<string | null> = data<string | null>(null)
    const iconVisible$ = data(true)

    const view = (
        <button
            className={`profileAvatar button${props.className ? ` ${props.className}` : ''}`}
            tabIndex={0}
            onclick={(event) => {
                event.stopImmediatePropagation()
                const opening = !props.headerMenuVisible.value
                if (opening) {
                    reportShellClick({ actionType: 'showSettingsOverflow', interactionType: 'TAP' })
                }
                props.headerMenuVisible.value = opening
            }}
            visible={isTv() ? signedIn() : authSettled()}
            aria-label={getString('dmusic_header_profile_menu_label')}
            title={getString('dmusic_header_profile_menu_label')}
            aria-haspopup="menu"
            aria-expanded={props.headerMenuVisible.map((v) => (v ? 'true' : 'false'))}
            onConnect={() => {
                const { add, dispose } = createDisposer()
                // Subscribe to the shared user-info observable so the
                // avatar reflects edits (avatar/name) immediately,
                // including overlay-driven optimistic updates from the
                // profile page.
                add(
                    signedIn().onData((isIn) => {
                        if (!isIn) imageUrl$.value = null
                    })
                )
                add(
                    getUserInfo().onData((info) => {
                        if (!signedIn().value || !info) {
                            imageUrl$.value = null
                            return
                        }
                        imageUrl$.value = pickProfileImageUrl(info)
                    })
                )
                add(
                    watchLoadableUrl(imageUrl$, (url) => {
                        view.style.backgroundImage = url ? `url("${url}")` : ''
                        view.classList.toggle('hasImage', !!url)
                        iconVisible$.value = !url
                    })
                )
                return dispose
            }}
        >
            <Icon
                icon="profileFilled"
                className="profileAvatarPlaceholder"
                aria-hidden="true"
                visible={iconVisible$}
            />
        </button>
    )
    return view
}
